
import { supabase } from "@/integrations/supabase/client";

// Interface for temporary file reference
export interface FileReference {
  isFileReference: true;
  key: string;
  name: string;
  type: string;
  size: number;
}

/**
 * Check if an object is a FileReference
 */
export function isFileReference(obj: any): obj is FileReference {
  return obj && 
         typeof obj === 'object' && 
         obj.isFileReference === true &&
         typeof obj.key === 'string' &&
         typeof obj.name === 'string';
}

/**
 * Upload a file to temporary storage in the /temp/ subdirectory
 * @param file File to upload
 * @returns A FileReference object or null if upload failed
 */
export async function uploadTemporaryFile(file: File): Promise<FileReference | null> {
  try {
    // Generate a unique file path in the temp subdirectory
    const fileExt = file.name.split('.').pop();
    const fileName = `temp/${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
    
    console.log("Uploading temporary file:", file.name, "to path:", fileName);
    
    // Upload file to the temp subdirectory in identity_docs bucket
    const { error: uploadError, data } = await supabase.storage
      .from('identity_docs')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });
      
    if (uploadError) {
      console.error("Upload error:", uploadError);
      return null;
    }
    
    console.log("Temporary file uploaded successfully to:", fileName);
    
    // Return a file reference
    return {
      isFileReference: true,
      key: fileName,
      name: file.name,
      type: file.type,
      size: file.size
    };
  } catch (error) {
    console.error("Error uploading temporary file:", error);
    return null;
  }
}

/**
 * Download a file from temporary storage
 * @param reference FileReference object
 * @returns A File object or null if download failed
 */
export async function downloadTemporaryFile(reference: FileReference): Promise<File | null> {
  try {
    console.log("Downloading temporary file from:", reference.key);
    
    // Download file from Supabase Storage - using identity_docs bucket
    const { data, error } = await supabase.storage
      .from('identity_docs')
      .download(reference.key);
      
    if (error || !data) {
      console.error("Download error:", error);
      return null;
    }
    
    console.log("File downloaded successfully, size:", data.size);
    
    // Convert blob to File
    const file = new File([data], reference.name, { 
      type: reference.type
    });
    
    return file;
  } catch (error) {
    console.error("Error downloading temporary file:", error);
    return null;
  }
}

/**
 * Move a file from temporary storage to permanent storage
 * @param tempFileKey The temporary file key
 * @param permanentPath The destination path for permanent storage
 * @returns boolean indicating success
 */
export async function moveTemporaryToPermanent(tempFileKey: string, permanentPath: string): Promise<boolean> {
  try {
    console.log("Moving file from temp storage:", tempFileKey, "to permanent:", permanentPath);
    
    // First, copy the file to the new location
    const { error: copyError } = await supabase.storage
      .from('identity_docs')
      .copy(tempFileKey, permanentPath);
      
    if (copyError) {
      console.error("Error copying file:", copyError);
      return false;
    }
    
    // Then delete the temporary file
    const { error: deleteError } = await supabase.storage
      .from('identity_docs')
      .remove([tempFileKey]);
      
    if (deleteError) {
      console.error("Error removing temporary file:", deleteError);
      // Don't fail the operation if cleanup fails
    }
    
    console.log("File moved successfully to permanent storage:", permanentPath);
    return true;
  } catch (error) {
    console.error("Error moving temporary file:", error);
    return false;
  }
}

/**
 * Create a FileList-like object from a FileReference
 * @param file File object
 * @returns An object that mimics a FileList with a single file
 */
export function createFileListLike(file: File): { 0: File, length: number, item: (index: number) => File | null } {
  return {
    0: file,
    length: 1,
    item: (index: number) => index === 0 ? file : null
  };
}

/**
 * Clean up temporary files after successful submission
 * @param references Array of FileReference objects to clean up
 */
export async function cleanupTemporaryFiles(references: FileReference[]): Promise<void> {
  try {
    // Filter to only include files in the temp directory
    const tempFilePaths = references
      .map(ref => ref.key)
      .filter(key => key.startsWith('temp/'));
    
    if (tempFilePaths.length === 0) {
      console.log("No temporary files to clean up");
      return;
    }
    
    console.log("Cleaning up temporary files:", tempFilePaths);
    
    const { error } = await supabase.storage
      .from('identity_docs')
      .remove(tempFilePaths);
      
    if (error) {
      console.error("Error cleaning up temporary files:", error);
    } else {
      console.log("Temporary files cleaned up successfully");
    }
  } catch (error) {
    console.error("Error during cleanup:", error);
  }
}

/**
 * Clean up expired temporary files (older than 24 hours)
 * This function can be called periodically to maintain storage
 */
export async function cleanupExpiredTemporaryFiles(): Promise<void> {
  try {
    console.log("Starting cleanup of expired temporary files");
    
    // List all files in the temp directory
    const { data: tempFiles, error: listError } = await supabase.storage
      .from('identity_docs')
      .list('temp/', {
        limit: 1000,
        sortBy: { column: 'created_at', order: 'asc' }
      });
      
    if (listError) {
      console.error("Error listing temporary files:", listError);
      return;
    }
    
    if (!tempFiles || tempFiles.length === 0) {
      console.log("No temporary files found for cleanup");
      return;
    }
    
    // Filter files older than 24 hours
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const expiredFiles = tempFiles.filter(file => 
      file.created_at && new Date(file.created_at) < oneDayAgo
    );
    
    if (expiredFiles.length === 0) {
      console.log("No expired temporary files to clean up");
      return;
    }
    
    // Delete expired files
    const filePaths = expiredFiles.map(file => `temp/${file.name}`);
    const { error: deleteError } = await supabase.storage
      .from('identity_docs')
      .remove(filePaths);
      
    if (deleteError) {
      console.error("Error deleting expired temporary files:", deleteError);
    } else {
      console.log(`Successfully cleaned up ${expiredFiles.length} expired temporary files`);
    }
  } catch (error) {
    console.error("Error during expired file cleanup:", error);
  }
}
